import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';
import { Web2ServiceService } from '../../../services/web2-service.service';
import { LOGIN_OPTION } from '../../../common/constants/common.constants';
import { WalletService } from '../../../services/wallet.service';
import { ApiService } from '../../../services/api.service';
import { ToastService } from '../../../services/toast.service';
import { HelperService } from '../../../services/helper.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { LoaderService } from '../../../services/loader.service';
import { NgOtpInputComponent, NgOtpInputConfig } from "ng-otp-input";
import { OTP_CONFIG_6_DIGIT, OTP_CONFIG_8_DIGIT } from '../../../services/otp-config';
import { emailValidator } from '../../directives/email-validator.directive';

declare const window: any;
@Component({
  selector: 'app-login-modal',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgOtpInputComponent],
  templateUrl: './login-modal.component.html',
  styleUrl: './login-modal.component.css'
})
export class LoginModalComponent implements OnInit {
  @Input() isOpen: boolean = false;
  @Output() closeFlag = new EventEmitter<boolean>();
  loginForm: FormGroup = new FormGroup({
    email: new FormControl("", [emailValidator])
  })
  email: string = '';
  operation: string = "login";
  otp: string = '';
  isOpenRecoveryModalShow: boolean = false;
  showDisableTFAStatus: boolean = false;
  showTfaSuccess: boolean = false;
  showContactSupport: boolean = false;
  isVerifying: boolean = false;
  readonly supportEmail: string = 'contact@blockchainx.tech';
  private successTimer: any = null;

  /**
   * The 2FA screens are shown as a bottom sheet below 575px, the plain login
   * screen keeps its centered dialog layout.
   */
  get isSheetView(): boolean {
    return this.operation !== 'login' || this.isOpenRecoveryModalShow
      || this.showDisableTFAStatus || this.showContactSupport;
  }

  /** Below 575px the recovery code is typed into a single field, so the
   *  Next button needs the same length check the OTP boxes enforce. */
  get isRecoveryCodeComplete(): boolean {
    return this.otp.trim().length === (this.recoveryConfig.length ?? 8);
  }

  constructor(
    private router: Router,
    private auth: AuthService,
    private helper: HelperService,
    private loader: LoaderService,
    private toastr: ToastService,
    private service: ApiService,
    private wallet: WalletService,
    private web2Service: Web2ServiceService
  ) { }

  otpConfig = OTP_CONFIG_6_DIGIT
  recoveryConfig = OTP_CONFIG_8_DIGIT



  ngOnInit(): void {

    // Commented reson : this is for 2fa auth flow which is handled in authenticator modal now only show the 2fa modal after login
    // let tfa_token: string | null = this.auth.getTempSessionToken();
    // if (tfa_token && !this.auth.checkTokenExpired(tfa_token))
    //   this.operation = 'tfa_auth';
  }

  async loginAction(option: string, type: string, provider: any = null) {
    let result: string | Array<string> | null | undefined = null;

    this.auth.setLoginOption(JSON.stringify({ option, type, provider }));
    try {
      if (option == LOGIN_OPTION["EMAIL_LINK"])
        result = await this.web2Service.loginWithPayload(LOGIN_OPTION["EMAIL_LINK"], { ...this.loginForm.getRawValue() });
      else if (option == LOGIN_OPTION["EMAIL_OTP"])
        result = await this.web2Service.loginWithPayload(LOGIN_OPTION["EMAIL_OTP"], { ...this.loginForm.getRawValue() });
      else if (option == LOGIN_OPTION["SOCIAL_LOGIN"] && provider && this.helper.isSocialLogin(provider))
        this.web2Service.loginWithSocial(provider);
      else if (option == LOGIN_OPTION["WALLET_LOGIN"] && provider && this.helper.isWalletLogin(provider)) {
        let providerResp = await this.wallet.connectWallet(provider);
        if (!providerResp) {
          const isMetaMaskLogin = provider === 'metaMask';
          const hasMetaMask = !!window?.ethereum?.isMetaMask;

          if (isMetaMaskLogin && !hasMetaMask) {
            this.toastr.error("MetaMask not found. Please install MetaMask.");
          } else {
            const walletName = isMetaMaskLogin ? 'MetaMask' : 'wallet';
            this.toastr.error(`Please make sure your wallet is unlocked and try again.`);
          }
          return;
        }
        result = this.wallet.getAddress();
        if (!result) {
          this.toastr.error("Unable to perform the operation. Please connect later");
          return;
        }

        // Check if user is already logged in and trying to connect a DIFFERENT wallet
        if (this.auth.isLoggedIn()) {
          const loggedInUser = this.auth.getUserData();
          const newlyConnectedAddress = Array.isArray(result) ? result[0] : result;
          if (loggedInUser?.public_address && newlyConnectedAddress.toLowerCase() !== loggedInUser.public_address.toLowerCase()) {
            this.toastr.error("Account mismatch detected. Logging out to maintain session integrity.");
            this.auth.clearSession(); // Full logout
            return;
          }
        }
      }

      if (result) {
        let token = Array.isArray(result) ? result[0] : result;
        this.login(token, option)
      }
    }
    catch (error: any) {
      console.log(error, "ERROR===================");
      const errorMsg = error?.message?.toLowerCase() || '';
      const errorCode = error?.code;

      if (errorCode === 4001 || errorMsg.includes('user denied') || errorMsg.includes('rejected')) {
        this.toastr.error("Connection was cancelled by the user.");
      } else if (errorCode === -32002 || errorMsg.includes('already pending')) {
        this.toastr.error("A connection request is already active in your wallet. Please check your wallet.");
      } else {
        this.toastr.error("Please make sure your wallet is unlocked and try again.");
      }
    }
  }

  /**
   * On OTP change handler
   * @param otp 
   */
  onOtpChange(otp: string) {
    this.otp = otp;
    if (this.otp.length === 6 && this.operation === 'tfa_auth') {
      this.verifyMFACode();
    } else if (this.otp.length === 8 && this.operation === 'recovery') {
      this.verifyRecoveryCode();
    }
  }

  /**
   * Verify Recovery Code
   * 
   * This function is used to disable the two-factor authentication for a user.
   * It takes the recovery code as input and verifies it. If the verification is successful,
   * it disables the two-factor authentication for the user and shows a success message.
   * If the verification fails, it shows an error message.
   */
  verifyRecoveryCode(): void {
    if (!this.isRecoveryCodeComplete) return;
    this.loader.show();
    let code: string = this.otp.trim()
    this.service
      .remove2FA(code)
      .subscribe((result: any) => {
        this.loader.hide();
        this.auth.removeTempSessionToken();
        this.toastr.success("Two-Factor Authentication disabled successfully");
        this.closeRecoveryModal();
        // keep the OTP screen hidden while the status screen is shown
        this.operation = "tfa_status";
        this.showDisableTFAStatus = true
      }, (error: HttpErrorResponse) => {
        this.loader.hide();
        this.toastr.error("Something went wrong. Please try again");
      })
  }

  login(resp: string, option: string) {
    const that = this;
    if (option == LOGIN_OPTION["EMAIL_LINK"]) {
      that.auth.setSessionToken(resp);
      that.service
        .authenticateWeb2User()
        .subscribe((result: any) => {
          if (result.data && result.data.two_factor_required) {
            this.auth.setTempSessionTOken(result.data.jwt_token);
            this.operation = "tfa_auth"
          }
          else
            that.allowLogin(result);
        }, (error: any) => {
          console.log(error);
          this.toastr.error("Something went wrong. Please try later");
        })
    }
    else if (option == LOGIN_OPTION["WALLET_LOGIN"])
      that.service
        .requestForNonce({ "public_address": resp }).toPromise()
        .then(async function (response: any) {
          let result = response.data;
          let signature = await that.wallet.createSignature(result.sign_message);
          return that.service.verifySignature({ "public_address": resp, "signature": signature }).toPromise()
        })
        .then(function (result) {
          that.allowLogin(result);
        })
        .catch(function (error: any) {
          console.log(error, "=========>>>>>>>>>>>>>");
          const errorMsg = error?.message?.toLowerCase() || '';
          if (error?.code === 4001 || errorMsg.includes('user denied') || errorMsg.includes('rejected') || errorMsg.includes('user cancelled')) {
            that.toastr.error("Connection was cancelled by the user.");
          } else if (error?.code === -32002 || errorMsg.includes('already pending')) {
            that.toastr.error("A connection request is already active in your wallet. Please check your wallet.");
          } else {
            that.toastr.error("Something went wrong. Please try again");
            that.wallet.disconnect();
          }
        })
  }

  /**
   * Closes the login modal and resets all the flags.
   */
  close() {
    if (this.successTimer) {
      clearTimeout(this.successTimer);
      this.successTimer = null;
    }
    this.isOpen = false;
    this.isOpenRecoveryModalShow = false;
    this.showDisableTFAStatus = false;
    this.showTfaSuccess = false;
    this.showContactSupport = false;
    this.isVerifying = false;
    this.operation = "login";
    this.otp = '';
    this.auth.removeTempSessionToken();
    this.closeFlag.emit(this.isOpen);
  }

  async enableTradeOptionForWeb2(): Promise<void> {
    let userdata: any = this.auth.getUserData();
    try {
      let signature: any = await this.web2Service.generateSignature(userdata.public_address);

      if (signature) {
        let parsedData = this.wallet.getSignatureParams(signature as `0x${string}`);
        console.log("Enabling trading for Web2 user in login modal components", parsedData);
        this.service.enableTrading(parsedData)
          .subscribe((result: any) => {
            userdata.enable_trading = true;
            this.auth.setUserData(userdata);
          }, (error: HttpErrorResponse) => {
            console.log(error);
            this.toastr.error("Failed to enable trading. Please try again.");
          })
      }
    } catch (error: any) {
      console.log(error);
      const errorMsg = error?.message?.toLowerCase() || '';
      if (error?.code === 4001 || errorMsg.includes('user denied') || errorMsg.includes('rejected') || errorMsg.includes('user cancelled')) {
        this.toastr.error("Signature request was cancelled.");
      }
    }
  }



  /**
   * Allow Login after MFA verification
   */
  allowLogin(result: any): void {
    this.toastr.success("LoggedIn Successfully");
    this.auth.login(result.data.jwt_token);
    const userData = {
      ...(result?.data?.user || {}),
      is_existing_user: result?.data?.is_existing_user
    };
    this.auth.setUserData(userData);
    this.close();

    // Defer navigation so it runs after modal close + Angular CD cycle completes
    const returnUrl = sessionStorage.getItem('loginReturnUrl');
    if (returnUrl) {
      sessionStorage.removeItem('loginReturnUrl');
      setTimeout(() => {
        // Angular ignores navigateByUrl if the target is the current URL.
        // Force navigation by first going to a dummy URL then to the target.
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigateByUrl(returnUrl);
        });
      }, 100);
    }

    if (!this.auth.isTradeEnabled()) {
      this.auth.sendOperationSignal("enable-trading");
    }
  }

  /**
   * Verify MFA Code
   */
  verifyMFACode(): void {
    if (this.otp.length !== 6 || this.isVerifying) return;
    this.isVerifying = true;
    this.loader.show();
    let code: string = this.otp
    const skipTfatoken = true
    this.service
      .verify2FA(code, skipTfatoken)
      .subscribe((result: any) => {
        this.loader.hide();
        this.isVerifying = false;
        this.auth.removeTempSessionToken();
        // Show the success screen before completing the login
        this.operation = "tfa_success";
        this.showTfaSuccess = true;
        this.successTimer = setTimeout(() => {
          this.successTimer = null;
          this.allowLogin(result);
        }, 1800);
      }, (error: HttpErrorResponse) => {
        this.loader.hide();
        this.isVerifying = false;
        this.toastr.error("Something went wrong. Please try again");
      })
  }
  /**
   * Opens the recovery modal
   */
  openRecoveryModal() {
    this.isOpenRecoveryModalShow = true;
    this.showContactSupport = false;
    this.otp = ''; // the 2FA code must not leak into the recovery field
    this.operation = 'recovery'; // Ensure operation changes so the other view might hide if needed, or overlay covers it
  }

  /**
   * Closes the recovery modal and resets the operation to 'tfa_auth'
   */
  closeRecoveryModal() {
    this.operation = 'tfa_auth';
    this.isOpenRecoveryModalShow = false;
    this.otp = '';
  }

  /**
   * Shows the support screen for users who also lost their recovery code.
   */
  openContactSupport() {
    this.isOpenRecoveryModalShow = false;
    this.showContactSupport = true;
  }

  /**
   * Returns from the support screen to the recovery code entry screen.
   */
  closeContactSupport() {
    this.showContactSupport = false;
    this.isOpenRecoveryModalShow = true;
    this.operation = 'recovery';
  }
}
